import { FC } from "react";
import { Database, CheckCircle, AlertTriangle, Clock, Shield } from "lucide-react"; // Add Shield to imports
import ShredItem from "@/components/architecture/ShredItem";
import { ShredResponse } from "@/services/MicroServiceService";

interface DataModelStepProps {
    dataModelShred: ShredResponse | undefined;
    generatingShredId: string | null;
    isAnyShredGenerating: boolean;
    isModelReady: boolean;
    isModelValidated: boolean;
    projectId: string;
    onGenerateSpecs: (shredId: string) => void;
    onExplore: (shredId: string) => void;
    onDelete: (shredId: string) => void;
}

const DataModelStep: FC<DataModelStepProps> = ({
    dataModelShred,
    generatingShredId,
    isAnyShredGenerating,
    isModelReady,
    isModelValidated,
    projectId,
    onGenerateSpecs,
    onExplore,
    onDelete
}) => {
    const getStatusInfo = () => {
        if (!dataModelShred) {
            return {
                icon: <Clock className="w-4 h-4"/>,
                text: "TO GENERATE",
                bgColor: "bg-blue-100",
                textColor: "text-blue-800"
            };
        }

        if (!dataModelShred.summary || dataModelShred.summary.trim() === "") {
            return {
                icon: <AlertTriangle className="w-4 h-4"/>,
                text: "NEEDS SPECS",
                bgColor: "bg-orange-100",
                textColor: "text-orange-800"
            };
        }

        if (!isModelValidated) {
            return {
                icon: <Shield className="w-4 h-4"/>,
                text: "NEEDS VALIDATION",
                bgColor: "bg-yellow-100",
                textColor: "text-yellow-800"
            };
        }

        return {
            icon: <CheckCircle className="w-4 h-4"/>,
            text: "VALIDATED & READY",
            bgColor: "bg-green-100",
            textColor: "text-green-800"
        };
    };

    const statusInfo = getStatusInfo();

    return (
        <div className="bg-white rounded-2xl border border-blue-200 shadow-sm overflow-hidden">
            <div className="bg-gradient-to-r from-blue-50 to-blue-100 px-6 py-4 border-b border-blue-200">
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                            isModelValidated ? 'bg-green-500' : dataModelShred ? 'bg-yellow-500' : 'bg-blue-500'
                        } text-white shadow-lg`}>
                            {isModelValidated ? <CheckCircle className="w-6 h-6"/> :
                             dataModelShred ? <Shield className="w-6 h-6"/> : <Database className="w-6 h-6"/>}
                        </div>
                        <div>
                            <div className="flex items-center space-x-3">
                                <span className={`px-3 py-1 rounded-full text-sm font-bold ${
                                    isModelValidated ? 'bg-green-100 text-green-800' : 
                                    dataModelShred ? 'bg-yellow-100 text-yellow-800' : 'bg-blue-100 text-blue-800'
                                }`}>
                                    STEP 1
                                </span>
                                <h3 className="text-xl font-bold text-gray-900">Data Model Foundation</h3>
                            </div>
                            <p className="text-gray-600 mt-1">
                                {isModelValidated
                                    ? "Validated and ready for feature development"
                                    : "Create, validate and approve your core data structures"
                                }
                            </p>
                        </div>
                    </div>

                    <div className="flex flex-col items-end">
                        <div className={`flex items-center space-x-2 px-3 py-2 ${statusInfo.bgColor} ${statusInfo.textColor} rounded-lg`}>
                            {statusInfo.icon}
                            <span className="text-sm font-medium">{statusInfo.text}</span>
                        </div>
                        {dataModelShred && !isModelValidated && dataModelShred.summary && (
                            <p className="text-xs text-yellow-600 mt-1 text-right">
                                Click "Explore" to validate
                            </p>
                        )}
                    </div>
                </div>
            </div>

            <div className="p-6">
                {dataModelShred ? (
                    <div className="grid grid-cols-12 gap-4">
                        <div className="col-span-12 lg:col-span-6">
                            <ShredItem
                                hideHeader={false}
                                hideTowProgress={false}
                                id={dataModelShred.shredId}
                                title={dataModelShred.name}
                                startDate={dataModelShred.startDate}
                                progress={dataModelShred.progress}
                                credit={dataModelShred.coinsUsed}
                                generationTime={dataModelShred.generationTime}
                                model={dataModelShred.model}
                                project={projectId}
                                step={dataModelShred.step}
                                status={dataModelShred.status}
                                summary={dataModelShred.summary}
                                isGeneratingSpecs={generatingShredId === dataModelShred.shredId}
                                isAnyShredGenerating={isAnyShredGenerating}
                                isModelReady={isModelReady}
                                isModelValidated={isModelValidated}
                                onGenerateSpecs={() => onGenerateSpecs(dataModelShred.shredId)}
                                onExplore={() => onExplore(dataModelShred.shredId)}
                                onDelete={onDelete}
                            />
                        </div>
                    </div>
                ) : (
                    <div className="text-center py-8 px-4 bg-blue-50/50 rounded-xl border-2 border-dashed border-blue-200">
                        <Database className="w-16 h-16 text-blue-400 mx-auto mb-4"/>
                        <h4 className="text-lg font-semibold text-blue-900 mb-2">Start with Data Model</h4>
                        <p className="text-blue-700 text-sm mb-4 max-w-md mx-auto">
                            Generate your data model shred first to establish the foundation of your service
                        </p>
                        <div className="inline-flex items-center px-4 py-2 bg-blue-100 text-blue-800 rounded-lg text-sm font-medium">
                            <div className="w-2 h-2 bg-blue-500 rounded-full mr-2 animate-pulse"></div>
                            Ready to generate
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default DataModelStep;